import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { RefreshIcon } from '../../../../../components/icons';

type Slot = {
  key: string;
  index: number;
  answer: string;
  caseSensitive?: boolean;
};

interface FillMissingTextProps extends BoxProps {
  text: string;
  slots: Slot[];
  debug?: boolean;
  showCorrect?: boolean;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: State;
  isViewOnly?: boolean;
}

type State = {
  answers: Record<string, { value: string; correct: boolean }>;
};

const FillMissingText: React.FC<FillMissingTextProps> = ({
  text,
  slots,
  debug,
  showCorrect,
  submit,
  reset,
  initialState,
  isViewOnly,
  ...props
}) => {
  const { t } = useTranslation();
  const textParts = text.split('xy_____yx');
  const [state, setState] = useState<State>({ answers: {} });
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  const indexMap = useMemo(() => {
    const map: Map<number, Slot> = new Map();

    slots.forEach((s) => {
      map.set(s.index, s);
    });

    return map;
  }, [slots]);

  const setInitialState = () => {
    setState({ answers: {} });
  };

  useEffect(() => {
    if (initialState != null) {
      setState(initialState);
      let correct = 0;

      Object.keys(initialState.answers).forEach((key) => {
        if (initialState.answers[key]!.correct) {
          correct += 1;
        }
      });
      setShowResults(true);
      setShownCorrect(correct);
    } else setInitialState();
  }, [slots, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state.answers).forEach((key) => {
      if (state.answers[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, slots.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    if (reset != null) reset();
  };

  const ret = (
    <Box {...props} style={{ textAlign: "center"}}>
      <pre>
        {textParts.map((part, index) => {
          if (indexMap.has(index)) {
            const slot = indexMap.get(index)!;
            const studentValue = state?.answers?.[slot.key]?.value ?? '';
            const correctAnswer = slot.answer ?? '';
            const displayValue =
              isViewOnly && showCorrect ? correctAnswer : studentValue;
            const minChars = Math.max(
              displayValue.length,
              showCorrect ? correctAnswer.length : 0,
              8,
            );

            return (
              <React.Fragment key={index}>
                {part} {' '}
                <Input
                  minW={`${minChars}ch`}
                  minH="32px"
                  h="24px"
                  w="auto"
                  htmlSize={minChars}
                  borderColor="gray.500"
                  _focus={{ borderColor: 'blue.500' }}
                  placeholder={
                    !isViewOnly && showCorrect
                      ? slot.answer
                      : undefined
                  }
                  display="inline-block"
                  isReadOnly={isViewOnly || showResults}
                  onChange={(e) => {
                    if (isViewOnly || showResults) return;
                    const newState = JSON.parse(JSON.stringify(state));
                    newState.answers[slot.key] = {
                      value: e.target.value,
                      correct: slot.caseSensitive
                        ? e.target.value.trim() === slot.answer
                        : e.target.value.trim().toLocaleLowerCase() ===
                          slot.answer.toLocaleLowerCase(),
                    };
                    setState(newState);
                  }}
                  value={displayValue}
                  key={slot.key}
                />
              </React.Fragment>
            );
          }
          return <React.Fragment key={index}>{part}</React.Fragment>;
        })}
      </pre>
      <VStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <div style={{ textAlign: 'center'}}>
              <Text fontWeight="medium">
              {t('plugin.associations.result', {
                correct: shownCorrect,
                total: slots.length,
              })}
              </Text>

              {!isViewOnly && (
                <Button
                  size="sm"
                  colorScheme="stemiBlue"
                  variant="outline"
                  onClick={_reset}
                  margin="10px"
                >
                  {t('plugin.interactiveQuiz.tryAgain')}
                </Button>
              )}
            
              {/* <span>
                {shownCorrect} / {slots.length}
              </span> */}

          </div>
        )}
          {!isViewOnly && !showResults && (
            <Button colorScheme="blue" onClick={_submit} margin="10px">
              {t('fill.submit')}
            </Button>
          )}
      
      </VStack>
    </Box>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return <>{ret}</>;
};

export default FillMissingText;
